"use strict";

import CardView from "/js/card-views/card-view.js";
import AppUtil from "/js/util/app-util.js";
import Constants from '/js/core/constants.js';
import FocusUtil from '/js/util/focus-util.js';

/**
 * Gallery card.
 */
export default class GalleryCardView extends CardView {

  static _TEMPLATE_STRING_1_THUMB = `\
      <div class='thumbs'\
        ><div class='thumb thumb-full'><img\
        ></div>\
      <div class='text'><%= text %></div>`
  static _template1Thumb = _.template(GalleryCardView._TEMPLATE_STRING_1_THUMB);

  static _TEMPLATE_STRING_2_THUMB = `\
      <div class='thumbs'\
        ><div class='thumb thumb-half'><img></div\
        ><div class='thumb thumb-half'><img\
        ></div>\
      <div class='text'><%= text %></div>`
  static _template2Thumb = _.template(GalleryCardView._TEMPLATE_STRING_2_THUMB);

  static _TEMPLATE_STRING_3_THUMB = `\
      <div class='thumbs'\
        ><div class='thumb thumb-third'><img></div\
        ><div class='thumb thumb-third'><img></div\
        ><div class='thumb thumb-third'><img\
        ></div>\
      <div class='text'><%= text %></div>`
  static _template3Thumb = _.template(GalleryCardView._TEMPLATE_STRING_3_THUMB);

  constructor(data) {
    super(data);

    this._$thumbHolder = this.$el.find('.thumbs');
    
    this._numThumbs = this.data.images.length;
    this._thumbs = [];
    const thumbDivs = this._$thumbHolder.find(".thumb");
    for (let i = 0; i < this._numThumbs; i++) {
      const $thumbDiv = $(thumbDivs[i]);
      const data = this.data.images[i];
      const thumb = new GalleryCardThumb($thumbDiv, data);
      thumb.$el.on("mouseup touchend touchcancel", e => this.#onThumb(e));
      FocusUtil.setEnterHandler(thumb.$el, e => this.#onThumb(e));
      this._thumbs.push(thumb);
    }
  }

  /* Override */
  makeContent() {
    if (!this.data.images || this.data.images.length === 0) {
      return "";  // Fail.
    }
    const o = {};
    o.text = CardView.makeHtmlFromJsonTextArray(this.data);
    let template;

    switch (this.data.images.length) {
      case 1:
        template = GalleryCardView._template1Thumb;
        break;
      case 2:
        template = GalleryCardView._template2Thumb;
        break;
      case 3:
      default:
        template = GalleryCardView._template3Thumb;  
      break;
    }
    return template(o);
  }

  /* Override */
  onFirstShow() {
    super.onFirstShow();
    this._thumbs.forEach((item) => {
      item.onFirstShow();
    });
  }

  /* Override */
  get isActive() {
    // When setter is overridden, getter must be as well (!!)
    return super.isActive;
  }

  /* Override */
  set isActive(b) {
    super.isActive = b;
    const tabIndex = b ? Constants.TABINDEX_CONTENT_GENERAL : -1;
    for (const thumb of this._thumbs) {
      thumb.$el.attr("tabindex", tabIndex);
    }
  }

  #onThumb(e) {
    FocusUtil.blurActiveElement();

    if (!(e.originalEvent instanceof KeyboardEvent)) {
      if (!this.dragDelegate.isStillClick) {
        // It's a mouse or gesture event that's gone beyond the point
        // of being considered a 'click', so ignore:
        return;
      }
    }

    e.stopImmediatePropagation();
    this.cancelDragAction();

    // If card has a transform, setting its position using css (ie, "posNo") fails.
    // There are a few bugs hiding here.
    this.$el.css("transform", "");

    let thumb;
    this._thumbs.forEach((item) => {
      if (item.$el[0] === e.currentTarget) {
        thumb = item;
      }
    });
    if (thumb) {
      this.$el.trigger("gallery-thumb", thumb);
    }
  }
}

export class GalleryCardThumb {

  /**
   * @param $el is the already-created thumbnail DOM element.
   * @param data is the image element from the card's json data.
   */
  constructor($el, data) {

    this._$el = $el;
    this._data = data;
    this._$image = $el.find("img");

    const url = AppUtil.makeLessonAssetPath(this._data.url);
    this._$image.attr("src", url);
    this._$image.attr("alt", this._data.accessibilityDescription);
    this._$image.on("load", $.proxy(this.onLoad, this));

    this._holderWidth = 0;
    this._holderHeight = 0;
    this._imageNaturalWidth = 0;
    this._imageNaturalHeight = 0;
  }

  get $el() {
    return this._$el;
  }

  get data() {
    return this._data;
  }

  get $image() {
    return this._$image;
  }

  onLoad(e) {
    this._imageNaturalWidth = this._$image[0].naturalWidth;
    this._imageNaturalHeight = this._$image[0].naturalHeight;
    this.finishInit();
  }

  onFirstShow() {
    this._holderWidth = this._$el.width();
    this._holderHeight = this._$el.height();
    this.finishInit();
  }

  finishInit() {
    if (!this._holderWidth || !this._imageNaturalWidth) {
      return;
    }

    // The display rect of the image, in pixels.
    let imageRectX = this.data.displayRect[0] * this._imageNaturalWidth;
    let imageRectY = this.data.displayRect[1] * this._imageNaturalHeight;
    let imageRectWidth = this.data.displayRect[2] * this._imageNaturalWidth;
    let imageRectHeight = this.data.displayRect[3] * this._imageNaturalHeight;

    const arImageRect = imageRectWidth / imageRectHeight;
    const arThumb = this._holderWidth / this._holderHeight;
    const isThumbShorter = (arImageRect > arThumb);

    const mult = isThumbShorter
        ? (this._holderHeight / imageRectHeight)
        : (this._holderWidth / imageRectWidth);

    const scaledImageWidth = this._imageNaturalWidth * mult;
    const scaledImageHeight = this._imageNaturalHeight * mult;

    this._$image.width(scaledImageWidth);
    this._$image.height(scaledImageHeight);

    let scaledImageRectWidth = imageRectWidth * mult;
    let scaledImageRectHeight = imageRectHeight * mult;
    let scaledImageRectX = imageRectX * mult * -1;
    let scaledImageRectY = imageRectY * mult * -1;
    if (isThumbShorter) {
      // adjust x
      scaledImageRectX += (this._holderWidth - scaledImageRectWidth) / 2;
    } else {
      // adjust y
      scaledImageRectY += (this._holderHeight - scaledImageRectHeight) / 2;
    }
    this._$image.css("left", scaledImageRectX + "px");
    this._$image.css("top", scaledImageRectY + "px");
  }
}
